import type {
  AdminStatsResponse,
  CarouselPayload,
  ChargedVia,
  ConsentAcceptInput,
  ContentFormatCode,
  CreditsResponse,
  ProfileResponse,
  ProfileUpdateInput,
  IdeaAdminCreate,
  IdeaAdminUpdate,
  IdeaQuestion,
  MarketingGoalCreate,
  MarketingGoalUpdate,
  Matryoshka,
  SessionStyleBody,
  TranscribeResponse,
  PackagesListResponse,
  PaymentCreateRequest,
  PaymentCreateResponse,
  PaymentResultResponse,
  PromoApplyRequest,
  PromoApplyResponse,
  ReferralClaimRequest,
  ReferralClaimResponse,
  ReferralInfoResponse,
  SessionsHistoryResponse,
  UniqJobResponse,
  UniqJobsListResponse,
} from '@masha/shared';
import { getInitData } from './tma';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseErrorResponse(res: Response): Promise<never> {
  let code = 'UNKNOWN';
  let message = `HTTP ${res.status}`;
  try {
    const data = (await res.json()) as { error?: { code?: string; message?: string } };
    if (data.error) {
      code = data.error.code ?? code;
      message = data.error.message ?? message;
    }
  } catch {
    // ignore
  }
  throw new ApiError(res.status, code, message);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Init-Data': getInitData(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) await parseErrorResponse(res);

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function uploadRequest<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'X-Init-Data': getInitData(),
    },
    body: form,
  });

  if (!res.ok) await parseErrorResponse(res);

  return (await res.json()) as T;
}

async function uploadBlobRequest<T>(path: string, blob: Blob, filename: string): Promise<T> {
  const form = new FormData();
  form.append('file', blob, filename);

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'X-Init-Data': getInitData(),
    },
    body: form,
  });

  if (!res.ok) await parseErrorResponse(res);

  return (await res.json()) as T;
}

/**
 * Multipart upload with progress (fetch has no upload progress). Errors map to
 * ApiError exactly like the fetch helpers so callers can switch on `code`.
 */
function uploadWithProgress<T>(
  path: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}${path}`);
    xhr.setRequestHeader('X-Init-Data', getInitData());
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        // non-JSON body
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data as T);
        return;
      }
      const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
      reject(
        new ApiError(xhr.status, err?.code ?? 'UNKNOWN', err?.message ?? `HTTP ${xhr.status}`),
      );
    };
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Network error'));
    xhr.onabort = () => reject(new ApiError(0, 'ABORTED', 'Upload aborted'));
    const form = new FormData();
    form.append('file', file, file.name);
    xhr.send(form);
  });
}

export interface IdeaListItem {
  id: number;
  title: string;
  thumbnail_url: string;
  matryoshka: Matryoshka;
}

export interface IdeasListResponse {
  ideas: IdeaListItem[];
  total: number;
  hasMore: boolean;
}

export interface IdeaPublicDetail {
  id: number;
  title: string;
  description: string;
  audience_action: string | null;
  matryoshka: Matryoshka;
  preview_video_urls: string[];
  marketing_goals: { id: number; label: string }[];
  format_code: ContentFormatCode;
}

export interface FormatDto {
  code: ContentFormatCode;
  name: string;
  description: string | null;
  sort_order: number;
}

export interface AdminFormatDto {
  id: number;
  code: ContentFormatCode;
  name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface CarouselStyleDto {
  code: string;
  name: string;
  description: string;
  preview_url: string | null;
  /** Whole sample deck in slide order; empty until the API has rendered it. */
  preview_urls: string[];
}

export interface CarouselRenderDto {
  style_code: string;
  image_urls: string[];
}

export interface AdminIdeaListItem {
  id: number;
  code: string;
  title: string;
  matryoshka: Matryoshka;
  thumbnail_url: string;
  is_active: boolean;
  sort_order: number;
  format_id: number;
}

export interface AdminIdeaDetail {
  id: number;
  format_id: number;
  code: string;
  title: string;
  description: string;
  audience_action: string | null;
  matryoshka: Matryoshka;
  thumbnail_url: string;
  preview_video_urls: string[];
  system_prompt: string;
  questions: IdeaQuestion[];
  is_active: boolean;
  sort_order: number;
  marketing_goal_ids: number[];
}

export interface AdminGoalResponse {
  id: number;
  code: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

export interface PublicGoal {
  id: number;
  label: string;
  sort_order: number;
}

export interface PublicGoalsResponse {
  goals: PublicGoal[];
}

export interface SessionDetailDto {
  id: number;
  user_id: number;
  idea_id: number | null;
  format_id: number;
  status: 'draft' | 'answering' | 'generating' | 'completed' | 'failed';
  answers: Record<string, string>;
  free_rewrites_used: number;
  created_at: string;
  updated_at: string;
}

export interface SessionProfileBlock {
  niche: string | null;
  product: string | null;
  target_audience: string | null;
}

export interface SessionsCreateResponse {
  session: SessionDetailDto;
  idea: { id: number; code: string; title: string; questions: IdeaQuestion[] };
  resumed: boolean;
  requires_niche: boolean;
  requires_product: boolean;
  requires_target_audience: boolean;
  profile: SessionProfileBlock;
}

export interface GenerationDto {
  id: number;
  session_id: number;
  content: string;
  model: string | null;
  tokens_used: number | null;
  is_free_rewrite: boolean;
  user_feedback: string | null;
  created_at: string;
  payload: CarouselPayload | null;
}

export interface SessionGenerateResponse {
  generation: GenerationDto;
  balance: number;
  session_status: 'completed';
  render: CarouselRenderDto | null;
  charged_via: ChargedVia;
}

export interface SessionRewriteResponse {
  generation: GenerationDto;
  balance: number;
  session_status: 'completed';
  is_free_rewrite: boolean;
  free_rewrites_used: number;
  render: CarouselRenderDto | null;
  charged_via: ChargedVia;
  /** Cost of the next rewrite, computed by the server. */
  rewrite_cost: 'free' | 'paid' | 'blocked';
}

export interface SessionGetResponse {
  session: SessionDetailDto;
  idea: { id: number; code: string; title: string; questions: IdeaQuestion[]; is_active: boolean };
  stale: boolean;
  last_generation: GenerationDto | null;
  rewrite_cost: 'free' | 'paid' | 'blocked';
  requires_niche: boolean;
  requires_product: boolean;
  requires_target_audience: boolean;
  profile: SessionProfileBlock;
  format_code: ContentFormatCode;
  style_code: string | null;
  nick: string | null;
  render: CarouselRenderDto | null;
}

export interface SessionDraftItem {
  id: number;
  idea: { id: number; code: string; title: string; questions_count: number };
  answered_count: number;
  updated_at: string;
}

export interface SessionsDraftsResponse {
  drafts: SessionDraftItem[];
}

export const api = {
  getProfile: (): Promise<ProfileResponse> => request('GET', '/profile'),
  updateProfile: (patch: ProfileUpdateInput): Promise<ProfileResponse> =>
    request('PATCH', '/profile', patch),
  acceptConsent: (body: ConsentAcceptInput): Promise<ProfileResponse> =>
    request('POST', '/profile/consent', body),

  listIdeas: (params: {
    format_code?: string;
    q?: string;
    matryoshka?: string;
    goal_ids?: string;
    limit?: number;
    offset?: number;
  }): Promise<IdeasListResponse> => {
    const qs = new URLSearchParams();
    if (params.format_code) qs.set('format_code', params.format_code);
    if (params.q) qs.set('q', params.q);
    if (params.matryoshka) qs.set('matryoshka', params.matryoshka);
    if (params.goal_ids) qs.set('goal_ids', params.goal_ids);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request('GET', `/ideas${suffix}`);
  },

  getIdea: (id: number): Promise<{ idea: IdeaPublicDetail }> => request('GET', `/ideas/${id}`),

  listGoals: (): Promise<PublicGoalsResponse> => request('GET', '/goals'),

  adminListIdeas: (): Promise<{ ideas: AdminIdeaListItem[] }> => request('GET', '/admin/ideas'),

  adminListFormats: (): Promise<{ formats: AdminFormatDto[] }> =>
    request('GET', '/admin/formats'),

  adminGetIdea: (id: number): Promise<{ idea: AdminIdeaDetail }> =>
    request('GET', `/admin/ideas/${id}`),

  adminCreateIdea: (body: IdeaAdminCreate): Promise<{ idea: AdminIdeaDetail }> =>
    request('POST', '/admin/ideas', body),

  adminUpdateIdea: (id: number, body: IdeaAdminUpdate): Promise<{ idea: AdminIdeaDetail }> =>
    request('PATCH', `/admin/ideas/${id}`, body),

  adminDeleteIdea: (id: number): Promise<{ success: true }> =>
    request('DELETE', `/admin/ideas/${id}`),

  adminUploadVideo: (file: File): Promise<{ url: string }> =>
    uploadRequest('/admin/ideas/upload-video', file),

  adminUploadThumbnail: (file: File): Promise<{ url: string }> =>
    uploadRequest('/admin/ideas/upload-thumbnail', file),

  adminListGoals: (): Promise<{ goals: AdminGoalResponse[] }> => request('GET', '/admin/goals'),

  adminGetStats: (): Promise<AdminStatsResponse> => request('GET', '/admin/stats'),

  adminCreateGoal: (body: MarketingGoalCreate): Promise<{ goal: AdminGoalResponse }> =>
    request('POST', '/admin/goals', body),

  adminUpdateGoal: (id: number, body: MarketingGoalUpdate): Promise<{ goal: AdminGoalResponse }> =>
    request('PATCH', `/admin/goals/${id}`, body),

  adminDeleteGoal: (id: number): Promise<{ success: true }> =>
    request('DELETE', `/admin/goals/${id}`),

  createSession: (
    body: { idea_id: number } | { idea_code: string },
  ): Promise<SessionsCreateResponse> => request('POST', '/sessions', body),

  getSession: (id: number): Promise<SessionGetResponse> => request('GET', `/sessions/${id}`),

  updateSessionAnswers: (
    id: number,
    answers: Record<string, string>,
  ): Promise<{ session: SessionDetailDto }> =>
    request('PATCH', `/sessions/${id}/answers`, { answers }),

  deleteSession: (id: number): Promise<void> => request('DELETE', `/sessions/${id}`),

  listDrafts: (): Promise<SessionsDraftsResponse> => request('GET', '/sessions/drafts'),

  listHistorySessions: (params: {
    limit?: number;
    offset?: number;
    format_code?: string;
  }): Promise<SessionsHistoryResponse> => {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    if (params.format_code) qs.set('format_code', params.format_code);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request('GET', `/sessions${suffix}`);
  },

  generateSession: (id: number): Promise<SessionGenerateResponse> =>
    request('POST', `/sessions/${id}/generate`),

  rewriteSession: (id: number, body: { feedback: string }): Promise<SessionRewriteResponse> =>
    request('POST', `/sessions/${id}/rewrite`, body),

  transcribeSession: (id: number, blob: Blob, filename: string): Promise<TranscribeResponse> =>
    uploadBlobRequest(`/sessions/${id}/transcribe`, blob, filename),

  listPackages: (): Promise<PackagesListResponse> => request('GET', '/packages'),

  createPayment: (body: PaymentCreateRequest): Promise<PaymentCreateResponse> =>
    request('POST', '/payments/create', body),

  getPaymentResult: (invoiceId: string): Promise<PaymentResultResponse> =>
    request('GET', `/payments/result/${encodeURIComponent(invoiceId)}`),

  applyPromo: (body: PromoApplyRequest): Promise<PromoApplyResponse> =>
    request('POST', '/promo/apply', body),

  getCredits: (): Promise<CreditsResponse> => request('GET', '/credits'),

  getReferralInfo: (): Promise<ReferralInfoResponse> => request('GET', '/referral'),

  claimReferral: (body: ReferralClaimRequest): Promise<ReferralClaimResponse> =>
    request('POST', '/referral/claim', body),

  listFormats: (): Promise<{ formats: FormatDto[] }> => request('GET', '/formats'),

  listCarouselStyles: (): Promise<{ styles: CarouselStyleDto[] }> =>
    request('GET', '/carousel/styles'),

  setSessionStyle: (
    id: number,
    body: SessionStyleBody,
  ): Promise<{ style_code: string; nick: string }> =>
    request('PATCH', `/sessions/${id}/style`, body),

  renderSession: (id: number, styleCode: string): Promise<{ render: CarouselRenderDto }> =>
    request('POST', `/sessions/${id}/render`, { style_code: styleCode }),

  deliverSession: (id: number): Promise<{ ok: boolean; reason?: string }> =>
    request('POST', `/sessions/${id}/deliver`),

  getUniqJobs: (): Promise<UniqJobsListResponse> => request('GET', '/uniq/jobs'),

  getUniqJob: (id: number): Promise<UniqJobResponse> => request('GET', `/uniq/jobs/${id}`),

  uploadUniqVideo: (file: File, onProgress: (pct: number) => void): Promise<UniqJobResponse> =>
    uploadWithProgress('/uniq/jobs', file, onProgress),
};
